import { NODES, parse, SCHEMAS } from '@workgrid/scim-filter'
import { get } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
// eslint-disable-next-line no-restricted-imports
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { defaultMemoize } from 'reselect'

import Page from '@/components/Page'
import {
  resetBulkTargetingStatus,
  resolveBulkTargetingAggregates,
  resolveBulkTargetingRuleSets,
  startNotification
} from '@/components/Publisher/actions'
import { getBulkTargetingStatus } from '@/components/Publisher/selectors'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { FETCH_ATTRIBUTES } from '@/pages/OrganizationUserAttributesPage/actionTypes'
import { fetchMultilingualNotificationById } from '@/pages/SpacePublisherPage/actions'
import { FETCH_NOTIFICATION } from '@/pages/SpacePublisherPage/actionTypes'
import { getNotificationById } from '@/pages/SpacePublisherPage/selectors'
import { resetRequests } from '@/store/actions'
import { isRequestError } from '@/store/selectors'
import { getAttributes, getTargetingRuleSetAttributes } from '@/utilities/UserUtilities/selectors'

import messages from './messages'

const errorsToHandle = [
  {
    type: FETCH_NOTIFICATION,
    data: { detailCode: 'PBCPFS' }
  },
  {
    type: FETCH_ATTRIBUTES,
    data: { detailCode: 'PBCPFA' }
  }
]

export const calculateAudienceSize = ({ aggregates, ruleSets }: any) => {
  let count = 0

  // @ts-expect-error
  Object.values(aggregates).forEach(({ filters }) => (count += filters.length))
  // @ts-expect-error
  Object.values(ruleSets).forEach(({ filters }) => (count += filters.length))

  return count
}

export const equalsIgnoreCase = (a: any, b: any) => a.localeCompare(b, undefined, { sensitivity: 'base' }) === 0

export const expression = (urn: any, value: any, operator: any) => `${urn} ${operator} ${JSON.stringify(value)}`

const findAttributeInCollection = defaultMemoize((collection, type) => {
  // default to the primary schema for unqualified attributes specifically for remapping of `email`, `guid` and `group`
  if (!type.startsWith('urn:')) {
    type = `${SCHEMAS.CORE_USER}:${type}`
  }

  return collection.find(({ urn }: any) => equalsIgnoreCase(urn, type))
})

// TODO Add tests for this scenario
export const mapNotificationAndAudience = defaultMemoize((notification, audience) => {
  if (get(notification, 'localizations')) {
    return {
      audience,
      notification: {
        ...notification,
        id: undefined,
        hiddenAfter: notification.hiddenAfter || '',
        audience: audience.map(({ type, value }: any) => ({ type, value }))
      }
    }
  }

  // TODO get rid of this after Global Language is set
  // set the new end timestamp using the offset between the cloned notifications start and end timestamps
  const newStartDisplayTime = moment()
  const daysDifference = moment(notification.hiddenAfter).diff(moment(notification.hiddenBefore), 'days')
  return {
    audience,
    notification: {
      audience: audience.map(({ type, value }: any) => ({ type, value })),
      category: notification.category,
      priority: notification.priority,
      detail: notification.detail || [],
      hiddenAfter: moment(newStartDisplayTime).add(daysDifference, 'days').toISOString(),
      hiddenBefore: newStartDisplayTime.toISOString(),
      location: notification.location,
      from: {
        name: get(notification, 'from.name', notification.source),
        iconUrl: get(notification, 'from.iconUrl')
      },
      summary: {
        body: notification.summary.body,
        image: {
          altText: get(notification.summary, 'image.altText', ''),
          url: get(notification.summary, 'image.url', '')
        },
        title: notification.summary.title
      }
    },
    settings: {}
  }
})

export const remapMember = defaultMemoize(({ type, value }) => {
  type = type.toLowerCase()

  // map member types to their attribute equivalents
  switch (type) {
    case 'email': {
      return { type: 'username', value }
    }
    case 'group': {
      return { type: 'groups.value', value }
    }
    case 'guid': {
      return { type: 'id', value }
    }
    // No default
  }

  // filter types must be interpreted to determine what attribute and value are being targeted
  const ast = parse(value)

  if (ast.type !== NODES.OPERATOR || !ast.left || !ast.right) {
    throw new Error('Unrecognizable targeting rule')
  }

  return { type: ast.left.value.toLowerCase(), value: ast.right.value }
})

// TODO Hookify (wait, maybe remove)
class SpacePublisherClonePage extends Component<any, any> {
  static displayName = 'SpacePublisherClonePage'

  static propTypes = {
    actions: PropTypes.shape({
      fetchAttributes: PropTypes.func.isRequired,
      fetchMultilingualNotificationById: PropTypes.func.isRequired,
      resetBulkTargetingStatus: PropTypes.func.isRequired,
      resetRequests: PropTypes.func.isRequired,
      resolveBulkTargetingAggregates: PropTypes.func.isRequired,
      resolveBulkTargetingRuleSets: PropTypes.func.isRequired,
      startNotification: PropTypes.func.isRequired
    }).isRequired,
    bulkTargetingStatus: PropTypes.shape({
      audience: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(['email', 'filter', 'group', 'guid']).isRequired,
          value: PropTypes.string.isRequired
        })
      ).isRequired,
      processed: PropTypes.number.isRequired
    }).isRequired,
    isFetchingError: PropTypes.bool.isRequired,
    notification: PropTypes.object,
    organizationAttributes: PropTypes.arrayOf(PropTypes.shape({ urn: PropTypes.string.isRequired })).isRequired,
    targetingRuleSets: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ urn: PropTypes.string.isRequired })))
      .isRequired
  }

  static defaultProps = {
    match: {},
    notification: {}
  }

  state = {
    audienceCount: 0
  }

  async componentDidMount() {
    await this.props.actions.resetBulkTargetingStatus()
    await Promise.all([
      this.props.actions.fetchAttributes(),
      this.props.actions.fetchMultilingualNotificationById(this.props.match.params.notificationId)
    ])
  }

  async componentDidUpdate(prevProps: any) {
    if (
      prevProps?.notification?.audience?.length !== this.props.notification?.audience?.length &&
      this.props.notification &&
      Array.isArray(this.props.notification.audience)
    ) {
      const audience = this.partitionByType(this.props.notification.audience)

      this.setState({ audienceCount: calculateAudienceSize(audience) })

      await Promise.all([
        this.props.actions.resolveBulkTargetingRuleSets(audience.ruleSets, false),
        this.props.actions.resolveBulkTargetingAggregates(audience.aggregates, false)
      ])
    }
    if (this.isAudienceProcessingComplete(prevProps)) {
      const { pathname } = this.props.location
      const { audience, notification, settings } = mapNotificationAndAudience(
        this.props.notification,
        this.props.bulkTargetingStatus.audience
      )

      await this.props.actions.startNotification(0, notification, settings, audience)
      this.props.history.replace(`${pathname.slice(0, Math.max(0, pathname.indexOf('/publisher')))}/publisher/builder`)
    }
  }

  findAttributeByType = (type: any) => {
    let attribute
    const ruleSetNames = Object.keys(this.props.targetingRuleSets)

    // search each ruleset's list of attributes
    for (const ruleSetName of ruleSetNames) {
      attribute = findAttributeInCollection(this.props.targetingRuleSets[ruleSetName], type)

      if (attribute) {
        return { ruleSet: ruleSetName, urn: attribute.urn }
      }
    }

    // MUST search for aggregate attribute matches AFTER rule sets or we're guaranteed to find a match
    attribute = findAttributeInCollection(this.props.organizationAttributes, type)

    if (attribute) {
      return { urn: attribute.urn }
    }

    throw new Error(`Unknown type column "${type}".`)
  }

  isAudienceProcessingComplete = (prevProps: any) => {
    const { processed } = this.props.bulkTargetingStatus
    const { audienceCount } = this.state

    return audienceCount > 0 && processed === audienceCount && processed > prevProps.bulkTargetingStatus.processed
  }

  partitionByType = (audience: any) => {
    const partitionCache = {}

    return audience.reduce(
      (partition: any, member: any) => {
        try {
          const { type, value } = remapMember(member)
          // @ts-expect-error
          let partitionKey = partitionCache[type]

          if (!partitionKey) {
            const { ruleSet, urn } = this.findAttributeByType(type)
            let groupBy = ruleSet ? undefined : urn

            // groups are targeted by their GUID but we need to show users group display names
            if (!ruleSet && urn === `${SCHEMAS.CORE_USER}:groups.value`) {
              groupBy = `${SCHEMAS.CORE_USER}:groups.display`
            }

            // @ts-expect-error
            partitionKey = partitionCache[type] = ruleSet ? 'ruleSets' : 'aggregates'
            partition[partitionKey][type] = {
              filters: [],
              groupBy,
              ruleSet,
              urn
            }
          }

          partition[partitionKey][type].filters.push(expression(partition[partitionKey][type].urn, value, 'eq'))
        } catch {
          /*
           * Swallow exception. Cloned notification must use an attribute that is no longer available to the
           * organization so we ignore this audience member.
           */
        }

        return partition
      },
      { aggregates: {}, ruleSets: {} }
    )
  }

  render() {
    return (
      <Page
        isLoading={true}
        loadingMessage={<FormattedMessage {...messages.loading} />}
        errorsToHandle={errorsToHandle}
      >
        <Page.Header title={<FormattedMessage {...messages.title} />} />
      </Page>
    )
  }
}

export { /** @deprecated */ SpacePublisherClonePage }

// @ts-expect-error
const mapStateToProps = (state, ownProps) => ({
  bulkTargetingStatus: getBulkTargetingStatus(state),
  isFetchingError: isRequestError(state, [FETCH_ATTRIBUTES, FETCH_NOTIFICATION]),
  organizationAttributes: getAttributes(state),
  notification: getNotificationById(state, ownProps.match.params.notificationId),
  targetingRuleSets: getTargetingRuleSetAttributes(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      fetchAttributes,
      resetBulkTargetingStatus,
      resetRequests,
      resolveBulkTargetingAggregates,
      resolveBulkTargetingRuleSets,
      startNotification,
      // V2
      fetchMultilingualNotificationById
    },
    dispatch
  )
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SpacePublisherClonePage))
